import React, { useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BLOG_ARTICLES from "../data/blogPosts";
import SEO from "../components/SEO";
import { blogPath, articleSchema, breadcrumbSchema, isPlaceholderImage, getBlogSeo } from "../lib/seo";

const BlogPost = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/blog\/?/, "").replace(/^\/|\/$/g, "");
  const article = BLOG_ARTICLES.find((p) => p.slug.replace(/^\/|\/$/g, "") === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article]);



  if (!article) return <Navigate to="/blog" replace />;

  const seo = getBlogSeo(article);
  const socialImage =
    seo.image || (isPlaceholderImage(article.image) ? undefined : article.image);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        title={seo.title}
        description={seo.description}
        path={blogPath(article.slug)}
        image={socialImage}
        type="article"
        schema={[
          articleSchema(article),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: article.title, path: blogPath(article.slug) },
          ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/blog"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-blog-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Link>
              <div className="text-xs text-[#009688] uppercase font-semibold tracking-wide mb-3">
                {article.date}
                {article.categories?.length ? ` • ${article.categories.join(", ")}` : ""}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {article.title}
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center bg-[#101F27] shadow-2xl"
                style={{ backgroundImage: `url('${article.image}')` }}
                role="img"
                aria-label={article.title}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article
            className="blog-content prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
          />
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
